import { useEffect, useMemo, useState, type CSSProperties } from 'react'
import { Icon } from '@iconify/react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { ApiClientError } from '@/lib/api/client'
import { fetchLandingCampaign } from '@/lib/landing'
import { applyLandingDocumentMeta } from '@/lib/landing-meta'
import { initializeMetaPixel, trackMetaPurchase } from '@/lib/meta-pixel'
import { initializeGoogleCampaignTracking } from '@/lib/google-tag'
import { useLandingCampaignPageView } from '@/hooks/useLandingCampaignPageView'
import { useLandingThankYouResume } from '@/hooks/useLandingThankYouResume'
import { LandingDefaultFooter } from '@/components/Landing/LandingDefaultFooter'
import { LandingProfileShell } from '@/pages/LandingNumbersPage/LandingProfileShell'
import { buildLandingInstitutionName } from '@/lib/landing-account-visual'
import { getCircularAvatarUrl } from '@/lib/profile-media'
import { resolveCampaignContactWhatsappHref } from '@/lib/campaign-contact'
import { resolveLandingSocialLinks } from '@/lib/campaign-social-links'
import { stripHtmlToPlainText } from '@/lib/landing-html'
import {
  OBRIGADO_ACCOUNT_REDIRECT_MS,
  buildAccountAutoAuthSearchParams,
  fetchPaymentAutoAuthTokenWithRetry,
  savePaymentAutoAuthCheckoutProof,
} from '@/lib/landing-payment-auto-auth'
import type { LandingCampaignResponse } from '@/types/landing'
import styles from './LandingObrigadoPage.module.css'

export function LandingObrigadoPage() {
  const navigate = useNavigate()
  const { slug } = useParams<{ slug: string }>()
  const [searchParams] = useSearchParams()
  const [data, setData] = useState<LandingCampaignResponse | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [redirectSecondsLeft, setRedirectSecondsLeft] = useState(
    Math.ceil(OBRIGADO_ACCOUNT_REDIRECT_MS / 1000)
  )
  const [autoAuthReady, setAutoAuthReady] = useState(false)
  const [autoAuthToken, setAutoAuthToken] = useState<string | null>(null)

  useEffect(() => {
    if (!slug) {
      setError('Slug da campanha não fornecido.')
      setIsLoading(false)
      return
    }

    let mounted = true

    const loadCampaign = async () => {
      setIsLoading(true)
      setError(null)
      try {
        const response = await fetchLandingCampaign(slug)
        if (!mounted) return
        setData(response)
      } catch (err) {
        if (!mounted) return
        if (err instanceof ApiClientError) {
          setError(
            err.status === 404
              ? 'Campanha não encontrada.'
              : 'Não foi possível carregar a campanha.'
          )
        } else {
          setError('Não foi possível carregar a campanha. Tente novamente em instantes.')
        }
      } finally {
        if (mounted) setIsLoading(false)
      }
    }

    void loadCampaign()
    return () => {
      mounted = false
    }
  }, [slug])

  const campaign = data?.data?.campaign ?? null
  const institution = data?.data?.institution ?? null
  const resume = useLandingThankYouResume(campaign?.id ?? null, searchParams.toString())
  const pageViewCampaignId = resume.phase === 'loading' ? null : (campaign?.id ?? null)

  useLandingCampaignPageView(pageViewCampaignId)

  useEffect(() => {
    if (campaign) {
      applyLandingDocumentMeta(campaign)
    }
  }, [campaign])

  useEffect(() => {
    if (resume.phase !== 'ready' || !campaign) {
      return
    }

    initializeMetaPixel(campaign.fb_pixel)
    initializeGoogleCampaignTracking({
      measurementId: campaign.gtm_measurement_id,
      googleTagManagerContainerId: campaign.g_pixel,
    })
    if (resume.snapshot?.meta_purchase_event_id) {
      trackMetaPurchase(campaign.fb_pixel, resume.snapshot.meta_purchase_event_id, {
        value: resume.snapshot.amount,
      })
    }
  }, [campaign, resume.phase, resume.snapshot])

  const checkoutProofToken = resume.snapshot?.checkout_token?.trim() ?? ''

  useEffect(() => {
    if (resume.phase !== 'ready' || !campaign?.id || !resume.snapshot?.payment_id) {
      return
    }

    if (checkoutProofToken === '') {
      setAutoAuthReady(true)
      return
    }

    let cancelled = false

    void fetchPaymentAutoAuthTokenWithRetry(
      campaign.id,
      resume.snapshot.payment_id,
      checkoutProofToken
    )
      .then((token) => {
        if (cancelled) {
          return
        }
        setAutoAuthToken(token)
        setAutoAuthReady(true)
      })
      .catch(() => {
        if (!cancelled) {
          setAutoAuthReady(true)
        }
      })

    return () => {
      cancelled = true
    }
  }, [campaign?.id, checkoutProofToken, resume.phase, resume.snapshot?.payment_id])

  const accountRedirectPath = useMemo(() => {
    if (!slug || !resume.snapshot?.payment_id) {
      return null
    }

    const params = buildAccountAutoAuthSearchParams({
      paymentId: resume.snapshot.payment_id,
      autoAuthToken,
    })
    const af = searchParams.get('af')?.trim()
    if (af) {
      params.set('af', af)
    }
    return `/campanha/${slug}/conta?${params.toString()}`
  }, [autoAuthToken, resume.snapshot?.payment_id, searchParams, slug])

  useEffect(() => {
    if (resume.phase !== 'ready' || !accountRedirectPath || !autoAuthReady) {
      return
    }

    const startedAt = Date.now()
    const tick = window.setInterval(() => {
      const elapsed = Date.now() - startedAt
      const remainingMs = Math.max(0, OBRIGADO_ACCOUNT_REDIRECT_MS - elapsed)
      setRedirectSecondsLeft(Math.max(1, Math.ceil(remainingMs / 1000)))
    }, 250)

    const redirectTimer = window.setTimeout(() => {
      if (resume.snapshot?.payment_id && checkoutProofToken) {
        savePaymentAutoAuthCheckoutProof(resume.snapshot.payment_id, checkoutProofToken)
      }
      navigate(accountRedirectPath, { replace: true })
    }, OBRIGADO_ACCOUNT_REDIRECT_MS)

    return () => {
      window.clearInterval(tick)
      window.clearTimeout(redirectTimer)
    }
  }, [
    accountRedirectPath,
    autoAuthReady,
    checkoutProofToken,
    navigate,
    resume.phase,
    resume.snapshot?.payment_id,
  ])

  if (isLoading) {
    return (
      <div className={styles.page}>
        <div className={styles.status} role="status">
          <Icon icon="mdi:loading" className={styles.statusIcon} aria-hidden />
          <p className={styles.statusText}>Carregando sua confirmação...</p>
        </div>
      </div>
    )
  }

  if (error || !campaign || !institution || !slug) {
    return (
      <div className={styles.page}>
        <div className={styles.status}>
          <Icon
            icon="mdi:alert-circle-outline"
            className={styles.statusIconError}
            aria-hidden
          />
          <p className={styles.statusText}>
            {error || 'Não foi possível carregar a campanha.'}
          </p>
          <button
            type="button"
            className={styles.primaryButton}
            onClick={() => window.location.reload()}
          >
            Tentar novamente
          </button>
        </div>
      </div>
    )
  }

  const titlePlain = stripHtmlToPlainText(campaign.title) || campaign.name
  const institutionName = buildLandingInstitutionName(institution)
  const institutionAvatarUrl = getCircularAvatarUrl(institution)
  const landingHref = `/campanha/${slug}`
  const accountHref = `/campanha/${slug}/conta?return=account`
  const contactWhatsappHref = resolveCampaignContactWhatsappHref({
    contactValue: campaign.public_contact_url,
    campaignTitle: titlePlain,
    whatsappButtonUrl: campaign.whatsapp_button_url,
    institutionPhone: institution.phone,
  })
  const landingSocialLinks = resolveLandingSocialLinks({
    campaignLinks: campaign.public_social_links,
    institutionLinks: institution.social_links,
  })

  const redirectDurationMs = OBRIGADO_ACCOUNT_REDIRECT_MS

  return (
    <>
      <LandingProfileShell
        campaign={campaign}
        institution={institution}
        homeHref={landingHref}
        passLabel="Pagamento confirmado"
      >
        <article className={styles.panel}>
            <p className={styles.panelIntro}>
              Obrigado pela sua participação. Em instantes você será direcionado para acessar sua
              conta na campanha <strong>{institutionName}</strong>.
            </p>

            {resume.phase === 'loading' && (
              <div className={styles.status} role="status">
                <Icon icon="mdi:loading" className={styles.statusIcon} aria-hidden />
                <p className={styles.statusText}>Confirmando seu pagamento...</p>
              </div>
            )}

            {resume.phase === 'error' && (
              <div className={styles.status}>
                <Icon
                  icon="mdi:alert-circle-outline"
                  className={styles.statusIconError}
                  aria-hidden
                />
                <p className={styles.statusText}>
                  {resume.errorMessage ||
                    'Não encontramos uma compra para continuar nesta página.'}
                </p>
                <button type="button" className={styles.primaryButton} onClick={resume.retry}>
                  Tentar novamente
                </button>
                <Link to={accountHref} className={styles.secondaryButton}>
                  Ir para minha conta
                </Link>
              </div>
            )}

            {(resume.phase === 'empty' ||
              (resume.phase === 'ready' && !resume.snapshot?.payment_id)) && (
              <div className={styles.status}>
                <Icon
                  icon="mdi:ticket-confirmation-outline"
                  className={styles.statusIconError}
                  aria-hidden
                />
                <p className={styles.statusText}>
                  Não encontramos uma compra para continuar nesta página. Você ainda pode acessar
                  sua conta manualmente.
                </p>
                <Link to={accountHref} className={styles.primaryButton}>
                  Ir para minha conta
                </Link>
              </div>
            )}

            {resume.phase === 'ready' && resume.snapshot?.payment_id && (
              <div
                className={styles.successStage}
                role="status"
                aria-live="polite"
                style={
                  {
                    '--redirect-duration': `${redirectDurationMs}ms`,
                  } as CSSProperties
                }
              >
                <div className={styles.successOrb} aria-hidden>
                  <div className={styles.successCore}>
                    <Icon icon="mdi:check-bold" aria-hidden />
                  </div>
                </div>

                <div className={styles.sparkRow} aria-hidden>
                  <span className={styles.spark} />
                  <span className={styles.spark} />
                  <span className={styles.spark} />
                </div>

                <p className={styles.redirectTitle}>Preparando seu acesso</p>
                <p className={styles.redirectCountdown}>
                  Redirecionando em{' '}
                  <strong>{redirectSecondsLeft}</strong>{' '}
                  {redirectSecondsLeft === 1 ? 'segundo' : 'segundos'}…
                </p>

                <div className={styles.progressTrack} aria-hidden>
                  <div className={styles.progressFill} />
                </div>

                {!autoAuthReady && (
                  <p className={styles.redirectHint}>Validando confirmação do pagamento…</p>
                )}

                {accountRedirectPath ? (
                  <Link to={accountRedirectPath} className={styles.primaryButton}>
                    Continuar agora
                  </Link>
                ) : null}
              </div>
            )}
        </article>
      </LandingProfileShell>

      <LandingDefaultFooter
        campaignTitle={titlePlain}
        institution={institution}
        institutionName={institutionName}
        institutionAvatarUrl={institutionAvatarUrl}
        socialLinks={landingSocialLinks}
        contactWhatsappHref={contactWhatsappHref}
        onAccountAction={() => {
          window.location.assign(accountHref)
        }}
      />
    </>
  )
}
